import {ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {classToClass, plainToClass} from 'class-transformer';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {FullCalendar} from 'primeng/fullcalendar';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from "@fullcalendar/list";

import {GrowlService, UserService} from '@app/core';
import {CalendarType} from '@app/shared/enums/calendar-type.enum';
import {DefaultOrg} from '@app/shared/enums/default-org.enum';
import {DialogStyle40} from '@app/shared/classes/common';
import {FrequencyType} from '@app/shared/enums/frequency-type.enum';
import {Roles} from '@app/shared/enums/roles.enum';
import {Validation} from '@app/shared/enums/validation.enum';
import {CommonTransformationService, DateService, Format, GrowlType, PrimeNgConfig, PrimeNgSupportService, TitleCasePipe} from '@app/shared';

import {environment} from '@env/environment';
import {AccountingFrequency, CalendarEvent, CalendarInfoFrequency, CalendarView, EventFrequency} from './calendar';
import {CalendarDataService} from './calendar-data.service';
import {endOfYear, isSameYear, parse} from 'date-fns';

/**
 * @author Jin Chun (Sep 2018)
 */

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.css'],
  providers: [CalendarDataService, ConfirmationService, TitleCasePipe]
})
export class CalendarComponent implements OnInit, OnChanges {

  // standard format and validation
  DialogStyle40 = DialogStyle40;
  Format: typeof Format = Format;
  Validation: typeof Validation = Validation;
  EventFrequency: typeof EventFrequency = EventFrequency;
  CalendarType: typeof CalendarType = CalendarType;
  DefaultOrg: typeof DefaultOrg = DefaultOrg;
  Roles: typeof Roles = Roles;
  PrimeNgConfig: typeof PrimeNgConfig = PrimeNgConfig;


  @Input() calendarType: CalendarType;        // input calendar type to load
  @Input() calendarYear: string;              // input calendar year to load

  @ViewChild('fc') fullCalendar: FullCalendar;// access underlying full calendar object as needed

  calendarEvents: CalendarEvent[];            // calendar events loaded from backend
  calendarOptions: any;                       // calendar option
  calendarView: string = CalendarView.DayGridMonth;  // calendar view - one of CalendarView options
  addCalendarEnabled: boolean = true;
  partialClose: boolean = false;              // show checkbox for allowing a timesheet peirod to be closed twice
  secondStartSetBefore: boolean = false;
  calendarInfoFrequency: CalendarInfoFrequency;    // stores map accounting frequency and an array of valid frequencies;

  isSystemCalendar: boolean = false;
  showStartDateCalendar: boolean = false;
  timesheetOrPayrollWeekly: boolean = false;
  timesheetOrPayrollMonthly: boolean = false;
  timesheetWeekly: boolean = false;
  accountingInvoicingCalendar: boolean = false;
  showStartPeriodNumber: boolean = false;
  showPayrollOffset: boolean = false;

  event: CalendarEvent;                       // either new or selected calendar event to edit
  dialogVisible: boolean = false;             // if true, display the dialog to gather or update the selected calendar event
  canUpdateCalendar: boolean = false;         // if true, can updated the calendar - based on user's role and type of calendar
  eventHeader: string;                        // header text for the dialog
  minCalendarDate: Date;                      // min date for the event - set to first day of calendarYear
  maxCalendarDate: Date;                      // max calendar date for holiday; may have to use FY calendar for compliance calendar
  defaultDate: Date;                          // default date for input calendar
  startDate: Date;                            // start date for timesheet and payroll periods


  frequencyDropDown: SelectItem[];            // frequency dropdown menu
  accountingFrequencyDropDown: SelectItem[];  // frequency dropdown for accounting calendar
  yearDropDown: SelectItem[];                 // year dropdown menu


  constructor(private cd: ChangeDetectorRef, private confirmationService: ConfirmationService, public user: UserService, private transform: CommonTransformationService,
              private titleCasePipe: TitleCasePipe, private dataService: CalendarDataService, private growl: GrowlService, private dateService: DateService,  private primeNgService: PrimeNgSupportService) {
  }

  /**
   * @desc load input calendar from the backend
   */
  ngOnChanges(changes: SimpleChanges): void {
    const roles: Array<Roles> = [];
    this.partialClose = false;

    // set permission whether user can update
    if (environment.roleCanUpdateCalendar[this.calendarType]) {
      roles.push(environment.roleCanUpdateCalendar[this.calendarType]);
      this.canUpdateCalendar = this.user.isAuthorized(roles);
    }

    if (this.calendarType) {
      this.isSystemCalendar = this.isOnelynkSystemCalendar(this.calendarType);
    }

    if (this.calendarType && this.calendarYear) {
      this.loadCalendar(this.calendarType, this.calendarYear);
    } else {
      console.warn("calendarType and calendarYear required to load calendar...");
    }
  }

  ngOnInit() {
    this.initializeData();
  }

  /**
   * @desc initialize data including dropdown menus for recurring frequency
   */
  initializeData(): void {

    this.frequencyDropDown = this.transform.convertEnumToDropDown(EventFrequency);
    this.setFrequencyDropDown(this.calendarType, this.calendarYear);


    this.calendarOptions = {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
      header: {
        left: 'prev,next today',
        center: 'title',
        right: 'listYear,dayGridMonth,timeGridWeek,timeGridDay'
      },
      buttonText: {
        listYear: 'Year',
        dayGridMonth: 'Month',
        timeGridWeek: 'Week',
        timeGridDay: 'Day',
      },
      defaultView: this.calendarView,
      editable: true,
      eventLimit:"4",
      dateClick: (e) =>  {
        this.handleDayClick(e);
      },
      eventClick: ($event) => {
        let selectedCalendar: CalendarEvent;
        let selectedCalendarId: number = $event.event ? Number($event.event.id) : null;
        this.handleEventClick(selectedCalendarId);
      }
    }


    // build year dropdown menu
    this.yearDropDown = [];
    let startYear = new Date().getFullYear() - 1;
    const endYear = new Date().getFullYear() + 2;

    while (startYear <= endYear) {
      this.yearDropDown.push({label: startYear.toString(), value: startYear.toString()});
      startYear++;
    }
  }

  /**
   * @desc return true if calendarType is one of the onelynk calendar that needs to be set annually
   * @param calendarType - input calendar type
   */
  isOnelynkSystemCalendar(calendarType: CalendarType): boolean {
    return (calendarType == CalendarType.Accounting || calendarType == CalendarType.Invoicing || calendarType == CalendarType.Payable || calendarType == CalendarType.Payroll || calendarType == CalendarType.Timesheet || calendarType == CalendarType.OrgBilling);
  }

  /**
   * @desc return true if calendarType is one of financial calendar types
   * @param calendarType
   */
  isOnelynkAccountingInvoicingCalendar(calendarType: CalendarType): boolean {
    return (calendarType == CalendarType.Accounting || calendarType == CalendarType.Invoicing || calendarType == CalendarType.Payable);
  }

  /**
   * @desc return true if calendarType is one of financial calendar types
   * @param calendarType
   */
  isMonthlyTimesheetPayroll(calendarType: CalendarType, frequency: FrequencyType): boolean {
    return (calendarType == CalendarType.Payroll || calendarType == CalendarType.Timesheet) && (frequency == FrequencyType.SemiMonthly || frequency == FrequencyType.Monthly);
  }

  /**
   * @desc return true if calendarType and frequency are biweekly or weekly timesheet or payroll periods
   * @param calendarType
   */
  isWeeklyTimesheetPayroll(calendarType: CalendarType, frequency: FrequencyType): boolean {
    return (calendarType == CalendarType.Payroll || calendarType == CalendarType.Timesheet) &&
      (frequency == FrequencyType.Weekly || frequency == FrequencyType.BiWeekly || frequency == FrequencyType.FourFourFive);
  }

  /**
   * @desc return true if calendarType and frequency are biweekly or weekly timesheet periods
   * @param calendarType
   */
  isWeeklyTimesheet(calendarType: CalendarType, frequency: FrequencyType): boolean {
    return (calendarType == CalendarType.Timesheet) &&
      (frequency == FrequencyType.Weekly || frequency == FrequencyType.BiWeekly || frequency == FrequencyType.FourFourFive);
  }

  /**
   * @desc if calendarType is either system or holiday, mark them allDay and add a day to the end date
   * @param events
   * @param calendarType
   */
  convertOnelynkCalendarDates(events: CalendarEvent[], calendarType: CalendarType) {
    if (this.isOnelynkSystemCalendar(calendarType) || calendarType == CalendarType.Holiday || calendarType == CalendarType.Compliance) {
      events.forEach(event => {
        event.end = event.end + "T24:00:00";
        event.allDay = true;
      });
    }
    return events;
  }

  /**
   * @desc set accountingFrequencyDropDown by existing accounting calendar's frequency
   * because once accounting calendars are created with a frequency, we cannot change the frequency type for future accounting calendars
   * if there is not existing accounting calendars, use AccountingFrequency
   * @param calendarType - calendar type to load
   * @param calendarYear - calendar year to load
   */
  setFrequencyDropDown(calendarType: CalendarType, calendarYear: string) {
    if (calendarType == CalendarType.Accounting && calendarYear) {
      this.accountingFrequencyDropDown = null;
      this.dataService.getAccountingCalendarFrequency(calendarYear, true).subscribe(
        res => {
          if (res) {
            this.calendarInfoFrequency = plainToClass(CalendarInfoFrequency, res as CalendarInfoFrequency);
            if (this.calendarInfoFrequency.accountingFrequencies.length > 0) {
              this.accountingFrequencyDropDown = this.transform.convertEnumToDropDown(this.calendarInfoFrequency.accountingFrequencies, false, null, true);
            } else {
              this.accountingFrequencyDropDown = this.transform.convertEnumToDropDown(AccountingFrequency);
            }
            if (this.calendarInfoFrequency.accountingStart) {
              // where calendar stats
              this.minCalendarDate = this.dateService.convertStringDateToPCalendarDate(this.calendarInfoFrequency.accountingStart);
              // what calenar shows
              this.defaultDate = this.minCalendarDate;
            }
            if (this.calendarInfoFrequency.accountingEnd) {
              // where calendar ends
              this.maxCalendarDate = this.dateService.convertStringDateToPCalendarDate(this.calendarInfoFrequency.accountingEnd);
            }
          }
        }
      );
    } else if ((calendarType == CalendarType.Timesheet || calendarType == CalendarType.Invoicing ||
      calendarType == CalendarType.Payroll || calendarType == CalendarType.Payable || calendarType == CalendarType.OrgBilling) && calendarYear) {
      this.frequencyDropDown = null;
      if (calendarYear) {
        this.dataService.getValidFrequencies(calendarType, calendarYear).subscribe(
          res => {
            if (calendarType == CalendarType.OrgBilling) {
              this.calendarInfoFrequency = plainToClass(CalendarInfoFrequency, res as CalendarInfoFrequency);
              this.frequencyDropDown = this.transform.convertEnumToDropDown(this.calendarInfoFrequency.targetFrequencies, false, null, true);
              this.minCalendarDate = this.dateService.convertStringDateToPCalendarDate(this.calendarInfoFrequency.orgBillingStart);
            } else if (res && res.targetFrequencies.length > 0 && res.accountingFrequencies.length > 0) {
              this.calendarInfoFrequency = plainToClass(CalendarInfoFrequency, res as CalendarInfoFrequency);
              if ((calendarType == CalendarType.Timesheet || calendarType == CalendarType.Payroll) && this.calendarInfoFrequency) {
                if (this.calendarInfoFrequency.accountingStart) {
                  // where calendar stats
                  this.minCalendarDate = this.dateService.convertStringDateToPCalendarDate(this.calendarInfoFrequency.accountingStart);
                  // what calenar shows
                  this.defaultDate = this.minCalendarDate;
                }
                if (this.calendarInfoFrequency.accountingEnd) {
                  // where calendar ends
                  this.maxCalendarDate = this.dateService.convertStringDateToPCalendarDate(this.calendarInfoFrequency.accountingEnd);
                  console.log("calendarInfoFrequency = ");
                  console.log(this.calendarInfoFrequency);
                }
              }
              this.frequencyDropDown = this.transform.convertEnumToDropDown(this.calendarInfoFrequency.targetFrequencies, false, null, true);
            }
          });
      }
    } else {
      this.accountingFrequencyDropDown = null;
      this.frequencyDropDown = null;
    }
  }

  /**
   * @desc show start calendar and payrollinput when
   * 1. create first year's timesheet periods, when this year has no payroll periods
   * 2. create first year's payroll periods, when this year has no timesheet periods
   * 3. edit timesheet or payroll periods' start date
   * @param calendarType
   * @param event -- user selected calendar info
   * @param calendarInfoFrequency -- db info
   */
  setShowStartCalendar(calendarType: CalendarType, event: CalendarEvent, calendarInfoFrequency: CalendarInfoFrequency) {
    this.accountingInvoicingCalendar = this.isOnelynkAccountingInvoicingCalendar(calendarType); // fixed the issue where start date does not show in accounting ui
    this.showStartDateCalendar = false;
    this.timesheetOrPayrollWeekly = false;
    this.timesheetWeekly = false;
    this.showStartPeriodNumber = false;
    this.showPayrollOffset = false;
    if (event.frequency) {
      this.timesheetOrPayrollMonthly = this.isMonthlyTimesheetPayroll(calendarType, event.frequency);
      this.timesheetOrPayrollWeekly = this.isWeeklyTimesheetPayroll(calendarType, event.frequency);
      this.timesheetWeekly = this.isWeeklyTimesheet(calendarType, event.frequency);
    }
    if (this.timesheetOrPayrollWeekly) {
      if (calendarInfoFrequency && !calendarInfoFrequency.inNextYear) {
        // first year of timesheet periods created, now create payroll periods --> showStartDateCalendar = false
        if (calendarInfoFrequency.payrollFrequency == null && calendarType == CalendarType.Payroll && calendarInfoFrequency.timesheetFrequency != null) {
          this.showStartDateCalendar = false;
          this.showPayrollOffset = false;
        }
        // first year of payroll periods created, now create timesheet periods --> showStartDateCalendar = true
        if (calendarInfoFrequency.payrollFrequency != null && calendarType == CalendarType.Timesheet && calendarInfoFrequency.timesheetFrequency == null) {
          this.showStartDateCalendar = true;
          this.showPayrollOffset = true;
        }
        // creating first year of timesheet periods, and no payroll periods have been created
        if (calendarType == CalendarType.Timesheet && calendarInfoFrequency.payrollFrequency == null && calendarInfoFrequency.timesheetFrequency == null
          || calendarType == CalendarType.Payroll && calendarInfoFrequency.timesheetFrequency == null && calendarInfoFrequency.payrollFrequency == null) {
          this.showStartDateCalendar = true;
          if (this.timesheetWeekly) {
            this.showPayrollOffset = true;
          }
        }
        this.showStartPeriodNumber = true;
      }
      if (event.id) {
        this.showStartDateCalendar = true;
        if (event.startPeriodNumber) {
          this.showStartPeriodNumber = true;
        }
        if (this.timesheetWeekly) {
          this.showPayrollOffset = true;
        }
      }
    }
  }


  /**
   * @desc: payroll periods have been created for this year with frequency types either bi-weekly or weekly (i.e., calendarInfoFrequency.payrollStart is not null),
   * and a user is creating/updating timesheet periods with frequency types of bi-weekly or weekly,
   * this function changes timesheetStart by payrollStart and user-defined payrollPeriodOffset
   * @param: event
   */
  onChangePayrollPeriodOffset(payrollPeriodOffset: number) {
    if (this.event.id == null) {
      if (this.calendarInfoFrequency && this.calendarInfoFrequency.payrollStart && this.calendarInfoFrequency.payrollFrequency
        && this.event && this.event.frequency
        && (this.event.frequency == FrequencyType.BiWeekly || this.event.frequency == FrequencyType.Weekly || this.event.frequency == FrequencyType.FourFourFive)
        && (this.calendarInfoFrequency.payrollFrequency == FrequencyType.BiWeekly
          || this.calendarInfoFrequency.payrollFrequency == FrequencyType.Weekly || this.calendarInfoFrequency.payrollFrequency == FrequencyType.FourFourFive)) {
        const tempDate = this.dateService.convertStringDateToPCalendarDate(this.calendarInfoFrequency.payrollStart);
        const dateNumber = tempDate.getDate() - payrollPeriodOffset;
        tempDate.setDate(dateNumber);
        this.event.start = this.dateService.convertDateToStringDate(tempDate);
      }
    }
  }

  /**
   * @desc: payroll periods have been created for this year with frequency types either bi-weekly or weekly (i.e., calendarInfoFrequency.payrollStart is not null),
   * and a user is creating/updating timesheet periods with frequency types of bi-weekly or weekly,
   * this function changes payrollPeriodOffset by user-defined payrollStart and timesheetStart
   * @param: timesheetStart
   */
  onChangeTimesheetStartDate(timesheetStart: string) {
    if (this.event.id == null) {
      if (this.calendarInfoFrequency && this.calendarInfoFrequency.payrollStart && this.calendarInfoFrequency.payrollFrequency
        && this.event && this.event.frequency
        && (this.event.frequency == FrequencyType.BiWeekly || this.event.frequency == FrequencyType.Weekly || this.event.frequency == FrequencyType.FourFourFive)
        && (this.calendarInfoFrequency.payrollFrequency == FrequencyType.BiWeekly || this.calendarInfoFrequency.payrollFrequency == FrequencyType.Weekly
          || this.calendarInfoFrequency.payrollFrequency == FrequencyType.FourFourFive)) {
        const payrollStartDate = this.dateService.convertStringDateToPCalendarDate(this.calendarInfoFrequency.payrollStart);
        const timesheetStartDate = this.dateService.convertStringDateToPCalendarDate(timesheetStart);
        const dateNumber = payrollStartDate.getDate() - timesheetStartDate.getDate();
        this.event.payrollPeriodOffset = dateNumber;
      }
    }
  }


  /**
   * @desc load calendar from the backend
   * @param calendarType - calendar type to load
   * @param calendarYear - calendar year to load
   */
  loadCalendar(calendarType: CalendarType, calendarYear: string) {

    if (calendarType && calendarYear) {
      this.addCalendarEnabled = true;

      /*
       * this.minDateWorkAuth = this.dateService.convertStringDateToPCalendarDate(this.selectedChargeCode.effectiveFrom);
       this.maxDateWorkAuth = this.dateService.convertStringDateToPCalendarDate(this.selectedChargeCode.effectiveThru);
       */

      // depends on the calendar type min/max calendar is set
      if (calendarType != CalendarType.Timesheet && calendarType != CalendarType.Payroll) {
        if (this.isOnelynkSystemCalendar(calendarType)) {
          // if we have to enter specific date for a system calendar, we may need to set minCalendarDate to be start day of the FY calendar
          this.minCalendarDate = this.dateService.dateWithOffset(calendarYear + "-01-01");
        } else {
          this.minCalendarDate = this.dateService.dateWithOffset(calendarYear + "-01-01");
        }
        this.maxCalendarDate = endOfYear(this.minCalendarDate);
      }

      this.setFrequencyDropDown(calendarType, calendarYear);

      // for invoicing/timesheet/payroll calendar, build a specific frequencyDropDown based on the accounting calendar
      // for timesheet calendar, if accounting calendar has frequency type of MONTHLY, then timesheet calendar has frequency types of MONTHLY, SEMI_MONTHLY, BI_WEEKLY, or WEEKLY
      if (calendarType == CalendarType.Invoicing || calendarType == CalendarType.Timesheet || calendarType == CalendarType.Payroll || calendarType == CalendarType.Payable) {
        this.dataService.getCalendarByType(CalendarType.Accounting, calendarYear).subscribe(
          res => {
            const accountCalendar = plainToClass(CalendarEvent, res.list);
            if (accountCalendar.length > 0 && (accountCalendar[0].frequency == FrequencyType.Monthly || accountCalendar[0].frequency == FrequencyType.FourFourFive)) {
              this.dataService.getCalendarByType(calendarType, calendarYear).subscribe(
                res => {
                  this.calendarEvents = this.convertOnelynkCalendarDates(plainToClass(CalendarEvent, res.list), calendarType);

                  this.changeCalendarView(calendarType, calendarYear);

                  if (this.calendarEvents.length > 0) {
                    this.addCalendarEnabled = false;
                  }
                },
                error => {
                  this.calendarEvents = [];
                  this.addCalendarEnabled = true;
                }
              );
            } else {
              this.calendarEvents = [];
              this.addCalendarEnabled = false;
              this.growl.add(GrowlType.Warn, `${this.titleCasePipe.transform(calendarType)} calendar cannot be configured until Accounting calendar is configured first!`);
            }
          },
          error => {
            this.calendarEvents = [];
            this.addCalendarEnabled = false;
            this.growl.add(GrowlType.Warn, `${this.titleCasePipe.transform(calendarType)} calendar cannot be configured until Accounting calendar is configured first!`);
          });
      } else {
        this.dataService.getCalendarByType(calendarType, calendarYear).subscribe(
          res => {
            this.calendarEvents = this.convertOnelynkCalendarDates(plainToClass(CalendarEvent, res.list), calendarType);

            this.changeCalendarView(calendarType, calendarYear);

            if ((calendarType == CalendarType.Accounting || calendarType == CalendarType.OrgBilling) && this.calendarEvents.length > 0) {
              this.addCalendarEnabled = false;
            }
          },
          error => {
            this.calendarEvents = [];
          }
        );
      }

    }
  }

  /**
   * @desc change calendar view to the specified input
   * @param calendarType - calendar type to change the view to
   */
  changeCalendarView(calendarType: CalendarType, calendarYear: string) {
    if (this.isOnelynkSystemCalendar(calendarType) || calendarType == CalendarType.Holiday || calendarType == CalendarType.Compliance) {

      this.fullCalendar.getCalendar().changeView(CalendarView.ListYear);

      // first day of the year; ToDo: this may not be optimal for FY calendar
      const date = parse(calendarYear, "yyyy", new Date());
      this.fullCalendar.getCalendar().gotoDate(date);

    }
  }

  setPartialCloseAndSecondStart(frequencyType: FrequencyType, calendarType: CalendarType) {
    this.event.partialClose = false;
    if ((frequencyType == FrequencyType.Weekly || frequencyType == FrequencyType.BiWeekly) && calendarType == CalendarType.Timesheet && this.calendarInfoFrequency.accountingFrequencies[0] == FrequencyType.Monthly) {
      this.partialClose = true;
    } else if (frequencyType == FrequencyType.BiWeekly && calendarType == CalendarType.Timesheet &&
      this.calendarInfoFrequency.accountingFrequencies[0] == FrequencyType.FourFourFive) {
      this.partialClose = true;
    } else {
      this.partialClose = false;
    }
    if (frequencyType == FrequencyType.SemiMonthly && this.calendarInfoFrequency.secondStart) {
      this.event.secondStart = this.calendarInfoFrequency.secondStart;
    } else {
      this.event.secondStart = null;
    }
    if (this.partialClose) {
      if (this.event.id) {
        this.event.partialClose = true;
      }
    }
    this.setShowStartCalendar(calendarType, this.event, this.calendarInfoFrequency);
  }

  /**
   * @desc copy federal holidays from the default org
   * @param year - year of the federal holidays to copy
   */
  onClickCopyHolidays(year: string) {
    this.confirmationService.confirm({
      message: `Please confirm to copy standard federal holidays for ${year}. Any custom holidays will be retained.`,
      header: `Copy Federal Holidays for ${year}`,
      icon: "fa fa-question-circle",
      accept: () => {
        this.dataService.copyDefaultHolidayCalendar(year).subscribe(
          res => {
            this.calendarEvents = plainToClass(CalendarEvent, res.list);
          },
          error => {
            this.calendarEvents = [];
          }
        );
      }
    });
  }

  /**
   * @desc allow user to create a new calendar event. show a dialog to gather information. set the start date from the input event.
   * this function is called when a user clicks the link for adding new calendars
   * @param e - input calendar event with a specified date.
   */

  // ToDo: Sophia to use event or remove it from the html call
  handleDayClick(event) {
    this.partialClose = false;

    // set secondStartSetBefore from calendarInfoFrequency.secondStart
    if (this.calendarInfoFrequency && this.calendarInfoFrequency.secondStart) {
      this.secondStartSetBefore = true;
    } else {
      this.secondStartSetBefore = false;
      if (this.event) {
        this.event.secondStart = null;
      }
    }

    // set default start date to be either today or first day of the year if the calendarYear is not this year
    // and if calendar type is not timesheet or payroll since default date has been set before calling this function
    if (this.calendarType != CalendarType.Timesheet && this.calendarType != CalendarType.Payroll) {
      const today = this.dateService.dateWithOffset(new Date());
      if (isSameYear(this.minCalendarDate, today)) {
        this.defaultDate = today;
      } else {
        this.defaultDate = this.minCalendarDate;
      }
    }

    // ToDo: filter yearDropDown to show only year without entries

    const formattedCalendarType = this.titleCasePipe.transform(this.calendarType);
    if (this.isSystemCalendar) {
      this.eventHeader = `Create ${formattedCalendarType} System Calendar`;
    } else if (this.calendarType == CalendarType.Holiday || this.calendarType == CalendarType.Compliance) {
      this.eventHeader = `Create a ${formattedCalendarType}`;
    } else {
      this.eventHeader = `Create a New Calendar Event`;
    }

    if (this.canUpdateCalendar) {
      this.event = new CalendarEvent();

      // set start and payroll period offset to UI fields from calendarInfoFrequency
      if (this.calendarInfoFrequency) {
        if (this.calendarType == CalendarType.Accounting && this.calendarInfoFrequency.accountingStart) {
          this.event.start = this.calendarInfoFrequency.accountingStart;
        } else if (this.calendarType == CalendarType.Invoicing && this.calendarInfoFrequency.invoicingNewYearStart) {
          this.event.start = this.calendarInfoFrequency.invoicingNewYearStart;
        } else if (this.calendarType == CalendarType.Payable && this.calendarInfoFrequency.payableNewYearStart) {
          this.event.start = this.calendarInfoFrequency.payableNewYearStart;
        } else if (this.calendarType == CalendarType.Timesheet) {
          if (this.calendarInfoFrequency.timesheetStart) {
            // put this first is to fix the start date for timesheet does not calculate from payroll start date
            this.event.start = this.calendarInfoFrequency.timesheetStart;
          } else if (this.calendarInfoFrequency.payrollStart) {
            // initial value when payroll to timesheet
            this.event.start = this.calendarInfoFrequency.payrollStart;
          } else if (this.calendarInfoFrequency.timesheetNewYearStart) {
            this.event.start = this.calendarInfoFrequency.timesheetNewYearStart;
          }
          this.event.payrollPeriodOffset = 0;
        } else if (this.calendarType == CalendarType.Payroll && this.calendarInfoFrequency.payrollStart) {
          // put this first is to fix the start date for payroll does not calculate from timesheet start date
          this.event.start = this.calendarInfoFrequency.payrollStart;
        } else if (this.calendarType == CalendarType.Payroll && this.calendarInfoFrequency.payrollNewYearStart) {
          this.event.start = this.calendarInfoFrequency.payrollNewYearStart;
        }
        if (this.calendarInfoFrequency.payrollPeriodOffset && this.calendarInfoFrequency.payrollPeriodOffset > 0
          && (this.calendarType == CalendarType.Timesheet || this.calendarType == CalendarType.Payroll)) {
          this.event.payrollPeriodOffset = this.calendarInfoFrequency.payrollPeriodOffset;
        }
      }

      if (this.calendarType == CalendarType.Accounting && this.accountingFrequencyDropDown && this.accountingFrequencyDropDown.length == 1) {
        this.event.frequency = this.accountingFrequencyDropDown[0].value;
      } else if (this.frequencyDropDown && this.frequencyDropDown.length == 1) {
        this.event.frequency = this.frequencyDropDown[0].value;
      }

      if (this.calendarInfoFrequency && this.calendarInfoFrequency.secondStart) {
        this.event.secondStart = this.calendarInfoFrequency.secondStart;
      }

      // ToDo: get rid these
      this.event.inherited = true;
      this.event.overridable = true;
      this.event.orgId = this.user.managingOrgId;

      this.event.subtype = this.calendarType;
      this.event.year = this.calendarYear;

      this.setShowStartCalendar(this.calendarType, this.event, this.calendarInfoFrequency);

      this.dialogVisible = true;


      //trigger detection manually as somehow only moving the mouse quickly after click triggers the automatic detection
      this.cd.detectChanges();
    }
  }

  /**
   * @desc allow user to update an existing calendar event. show a dialog to gather information.
   * @param e - input calendar event
   */
  handleEventClick(selectedCalendarId?: number) {
    // need to check permission

    this.event = new CalendarEvent();

    let selectedCalendar = this.calendarEvents.find(ce => ce.id === selectedCalendarId);


    if (selectedCalendar) {
      this.event = classToClass(selectedCalendar);

      // ToDo need to add this to the DB
      // this.event.allDay = event.calEvent.allDay;

      // onelynk specific attributes

      const formattedCalendarType = this.titleCasePipe.transform(this.event.subtype);
      if (this.isOnelynkSystemCalendar(this.event.subtype)) {
        this.eventHeader = `Update ${formattedCalendarType} system calendar`;
      } else if (this.calendarType == CalendarType.Holiday) {
        this.eventHeader = `Update ${formattedCalendarType}`;
      } else {
        this.eventHeader = `Update an existing calendar event`;
      }

      this.setShowStartCalendar(this.event.subtype, this.event, this.calendarInfoFrequency);

      /**
       * commented out the else part because this logic will set the end date wrongly for compliance and holiday calendars
       * when updating such calendars
       * e.g. if an existing compliance calendar has a start date of '2020-01-01', if we edit this calendar and change the start date to
       * '2020-03-01', this part of code will set the end date to '2020-01-01' in the payload, which is wrong
       else {
        // end is null if allDay is true. set end = start;
        if (event.calEvent.allDay) {
          this.event.end = start.format();
        }
      }
       */


      /**
       * since secondStart attribute is needed for editing a calendar with semi-monthly frequency type
       * and secondStart is not part of any calendar payloads, so we use the following function to calculate the value
       * if a picked calendar has frequency type of semi-monthly, we look at the calendar's period number, if the period number is
       * odd then this calendar is the first calendar in a month, and the secondStart can be calculated as (the number of the days in a month for the end date) plus one
       * if the period number is even then this calendar is the second calendar in a month, and the secondStart will be the number of days in a month for the start date.
       * e.g., if a timesheet calendar which has semi-monthly frequency type and period number 1, and if the end date is '2020-01-15', then the secondStart is 16.
       * the timesheet calendar right after this timesheet calendar will have period 2 and start date of '2020-01-16', then the secondStart is 16 as well.
       */
      if (selectedCalendar.frequency == FrequencyType.SemiMonthly) {
        if (this.calendarInfoFrequency && this.calendarInfoFrequency.secondStart) {
          this.event.secondStart = this.calendarInfoFrequency.secondStart;
        } else {
          if (this.event.period % 2 == 1) {
            this.event.secondStart = Number(this.event.end.slice(-2));
          } else {
            const startDay = Number(this.event.start.slice(-2));
            this.event.secondStart = startDay - 1;
          }
        }
      }

      /**
       * since partialClose attribute is needed for editing a timesheet calendar
       * call the function of setPartnerClose to show the checkbox
       * when timesheet periods have frequency types either bi-weekly and weekly
       * need to set the user-defined payrollPeriodOffset
       */
      if (selectedCalendar.subtype == CalendarType.Timesheet) {
        this.setPartialCloseAndSecondStart(selectedCalendar.frequency, selectedCalendar.subtype);
        if ((selectedCalendar.frequency == FrequencyType.Weekly || selectedCalendar.frequency == FrequencyType.BiWeekly || selectedCalendar.frequency == FrequencyType.FourFourFive)) {
          this.event.payrollPeriodOffset = selectedCalendar.payrollPeriodOffset;
        }
      }

      // this.event.allDay = selectedCalendar.allDay;
    }

    this.dialogVisible = true;
    //trigger detection manually as somehow only moving the mouse quickly after click triggers the automatic detection
    this.cd.detectChanges();
  }

  /**
   * @desc save the input calendar event to backend
   * @param event - event that may be new or has been changed
   */
  saveEvent(event: CalendarEvent) {

    if (event) {
      // ToDo: backend should have this field
      delete event.allDay;
      delete event.partialClose;

      if (this.isOnelynkSystemCalendar(event.subtype)) {
        this.dataService.saveCalendarByType(event).subscribe(res => {
          this.growl.add(GrowlType.Success, `${event.subtype} system calendar saved/updated`);
          this.calendarEvents = this.convertOnelynkCalendarDates(plainToClass(CalendarEvent, res.list), event.subtype);
          if (this.calendarEvents.length > 0) {
            this.addCalendarEnabled = false;
          }
        });
      } else {
        // for holiday, set end date to be equal to start date
        if (event.subtype == CalendarType.Holiday || event.subtype == CalendarType.Compliance) {
          event.end = event.start;
        }
        this.dataService.saveCalendarEvent(event).subscribe(res => {
          if (event.id) {
            this.growl.add(GrowlType.Success, `${event.subtype} calendar updated`);
            const index: number = this.calendarEvents.findIndex(ev => ev.id == event.id);
            if (index >= 0) {
              this.calendarEvents = this.primeNgService.replaceByIndex(this.calendarEvents,  plainToClass(CalendarEvent, res as CalendarEvent), index);
            }
          } else {
            this.growl.add(GrowlType.Success, `${event.subtype} calendar saved`);
            this.calendarEvents = this.primeNgService.push(this.calendarEvents,  plainToClass(CalendarEvent, res as CalendarEvent));
          }
          this.calendarEvents = this.convertOnelynkCalendarDates(this.calendarEvents, event.subtype);
        });
      }
    }
    this.dialogVisible = false;
  }

  /**
   * @desc delete the event from the backend and also from the calendarEvents
   * @param event - the event to be deleted from the calendar
   */
  deleteEvent(event: CalendarEvent) {
    if (event.id) {
      if (this.isOnelynkSystemCalendar(event.subtype)) {
        this.dataService.deleteCalendarByType(event.subtype, event.year).subscribe(res => {
          this.calendarEvents = [];
          this.addCalendarEnabled = true;
          this.growl.add(GrowlType.Success, `${event.subtype} system calendar deleted`);
          this.setFrequencyDropDown(event.subtype, event.year);
        });
      } else {
        const index = this.calendarEvents.findIndex(ev => ev.id == event.id);
        if (index >= 0) {
          this.dataService.deleteCalendarEvent(event.id).subscribe(res => {
            this.calendarEvents = this.primeNgService.deleteByIndex(this.calendarEvents, index);
            this.growl.add(GrowlType.Success, `${event.title} deleted`);
          });
        }
      }
    }
    this.dialogVisible = false;
  }
}
